import { useSyncExternalStore } from 'react';

export type ThemeMode = 'light' | 'dark';

const THEME_STORAGE_KEY = 'sien:theme';
const THEME_CHANGE_EVENT = 'sien-theme-change';
let currentTheme: ThemeMode = 'light';

const isThemeMode = (value: unknown): value is ThemeMode => value === 'light' || value === 'dark';

export function getStoredTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    currentTheme = isThemeMode(stored) ? stored : currentTheme;
    return currentTheme;
  } catch {
    return currentTheme;
  }
}

export function applyTheme(theme: ThemeMode = getStoredTheme()): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
}

export function initializeTheme(): void {
  applyTheme(getStoredTheme());
}

export function setStoredTheme(theme: ThemeMode): void {
  currentTheme = theme;
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Storage can fail in private contexts; still apply the theme for this session.
    }
  }
  applyTheme(theme);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: { theme } }));
  }
}

function subscribeTheme(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handleStorage = (event: StorageEvent) => {
    if (event.key !== THEME_STORAGE_KEY) return;
    applyTheme(getStoredTheme());
    listener();
  };
  window.addEventListener(THEME_CHANGE_EVENT, listener);
  window.addEventListener('storage', handleStorage);
  return () => {
    window.removeEventListener(THEME_CHANGE_EVENT, listener);
    window.removeEventListener('storage', handleStorage);
  };
}

function getThemeSnapshot(): ThemeMode {
  return getStoredTheme();
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribeTheme, getThemeSnapshot, () => 'light');
  const setTheme = (nextTheme: ThemeMode) => setStoredTheme(nextTheme);
  const toggleTheme = () => setStoredTheme(theme === 'dark' ? 'light' : 'dark');
  return { theme, setTheme, toggleTheme, isDark: theme === 'dark' };
}
